import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { generatePath, NavLink, useHistory } from "react-router-dom";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { createPortal } from "react-dom";
import { Routes } from "router/config";
import { useTranslation } from "locales";
import { ChatFolder } from "hooks/useChatFolders";
import { useDraftText } from "hooks/useDrafts";
import { getBcp47Locale, formatCallDurationLocalized } from "lib/dateLocale";
import emitter, { EVENTS, SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import { MediaFilesController } from "lib/MediaFileController";
import { cleanMessagePreview } from "lib/messageMetadata";
import { removeCachedThread } from "lib/offlineCache";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import useController from "./useController";
import "./style.scss";

interface Props {
  messageThread: MessageThread;
  authUser: User;
  onDeleted?: () => void;
  folders?: ChatFolder[];
  threadFolderIds?: string[];
  onToggleThreadInFolder?: (folderId: string, threadId: string) => void;
  onCreateFolder?: (name: string) => Promise<ChatFolder | null> | void;
}

function formatThreadTime(time: string, t: (key: string) => string): string {
  const date = new Date(time);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() >= today.getTime()) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  if (msgDate.getTime() >= yesterday.getTime()) {
    return t("Yesterday");
  }
  return date.toLocaleDateString(getBcp47Locale(), { day: "numeric", month: "short" });
}

function MessageThreadItem({
  messageThread,
  authUser,
  onDeleted,
  folders,
  threadFolderIds,
  onToggleThreadInFolder,
  onCreateFolder,
}: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { senderUser } = useController(messageThread, authUser);
  const draftText = useDraftText(String(messageThread.id));
  const isGroup = messageThread.subject?.subjectType === "group";
  const isOnline = !isGroup && senderUser?.onlineStatus?.isOnline;
  const groupName = isGroup ? messageThread.groupName || null : null;
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  // Post-measurement adjusted position. Starts null on each menu open; the
  // useLayoutEffect below runs synchronously after the menu commits to DOM,
  // measures the real menu height (depends on folder count + submenu state),
  // and flips the menu upward if it would overflow the bottom of the viewport.
  // Without this, the chat list's right-click menu got cropped at the bottom
  // (previously we just clamped y to window.innerHeight - 200, a hardcoded
  // guess that didn't match the actual menu height).
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [showFolderSub, setShowFolderSub] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchMoved = useRef(false);

  // Measure the actual menu rect and flip / clamp to keep it fully inside
  // the viewport. Runs after every change that can resize the menu:
  // initial open, folder-submenu expand/collapse, new-folder-input toggle.
  useLayoutEffect(() => {
    if (!ctxMenu) {
      setMenuPos(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const PADDING = 8;
    let top = ctxMenu.y;
    let left = ctxMenu.x;
    // Bottom overflow → pin the menu so its bottom edge sits PADDING px
    // above the viewport bottom (flip-up). Never let top go negative.
    if (top + rect.height + PADDING > window.innerHeight) {
      top = Math.max(PADDING, window.innerHeight - rect.height - PADDING);
    }
    // Right overflow → pull left edge inward (mirrors the folder-submenu
    // `__left` flip already handled in JSX).
    if (left + rect.width + PADDING > window.innerWidth) {
      left = Math.max(PADDING, window.innerWidth - rect.width - PADDING);
    }
    // Only update if the values actually changed — prevents an infinite
    // setState loop when the adjusted position doesn't differ.
    setMenuPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
    // `!!onCreateFolder` covers the case where the parent toggles whether
    // the "+ New folder" inline action is offered — that flips the
    // folder-parent button's conditional render and thus the menu height.
  }, [ctxMenu, showFolderSub, showNewFolderInput, folders?.length, !!onCreateFolder]);

  // Close context menu on any outside click/touch
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e: MouseEvent | TouchEvent) => {
      // Don't close if click is inside the menu
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      setCtxMenu(null);
      setShowFolderSub(false);
      setShowNewFolderInput(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [ctxMenu]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (ctxMenu) return;
      // Raw click position — the useLayoutEffect above flips/clamps to fit
      // the viewport after the menu's real height is known.
      setCtxMenu({ x: e.clientX, y: e.clientY });
      setShowFolderSub(false);
      setShowNewFolderInput(false);
    },
    [ctxMenu]
  );

  // Long-press for mobile: open context menu on touch-hold (~500ms)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchMoved.current = false;
    const touch = e.touches[0];
    const x = touch.clientX;
    const y = touch.clientY;
    longPressTimer.current = setTimeout(() => {
      e.preventDefault();
      // Raw touch position — useLayoutEffect adjusts after measurement.
      setCtxMenu({ x, y });
      setShowFolderSub(false);
      setShowNewFolderInput(false);
    }, 500);
  }, []);

  const handleTouchMove = useCallback(() => {
    touchMoved.current = true;
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      // If context menu is open, prevent navigation
      if (ctxMenu) {
        e.preventDefault();
      }
    },
    [ctxMenu]
  );

  const handleHideChat = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCtxMenu(null);
      if (!window.confirm(t("Hide this chat? You can find it in Archived chats."))) return;
      try {
        await providers.MessageThreadsProvider.hide({ id: messageThread.id });
        // Evict from IndexedDB so the archived chat doesn't flash back into the
        // main list on the next reload before the fresh (hidden:false) fetch.
        removeCachedThread(String(messageThread.id)).catch(() => {});
        emitter.emit(EVENTS.THREADS_REFRESH);
        if (onDeleted) onDeleted();
        history.push("/messages");
      } catch (err) {
        console.error("Failed to hide chat:", err);
      }
    },
    [messageThread.id, t, onDeleted, history]
  );

  const handleDeleteChat = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCtxMenu(null);
      if (!window.confirm(t("Delete this chat and all messages permanently?"))) return;
      try {
        await providers.MessageThreadsProvider.remove({ id: messageThread.id });
        // Evict from IndexedDB so the deleted chat doesn't flicker back on reload.
        removeCachedThread(String(messageThread.id)).catch(() => {});
        emitter.emit(EVENTS.THREADS_REFRESH);
        if (onDeleted) onDeleted();
        history.push("/messages");
      } catch (err) {
        console.error("Failed to delete chat:", err);
      }
    },
    [messageThread.id, t, onDeleted, history]
  );

  const handleLeaveGroup = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCtxMenu(null);
      const groupId = messageThread.subject?.subjectId;
      if (!groupId) return;
      if (!window.confirm(t("Leave this group?"))) return;
      // Leave group FIRST
      try {
        await providers.GroupsProvider.leaveGroup(groupId);
      } catch (err) {
        console.error("Failed to leave group:", err);
        return; // Don't proceed if leave failed
      }
      // System message after successful leave (best-effort, fire-and-forget)
      providers.MessagesProvider.sendExtendedMessages({
        data: {
          text: `${SYSTEM_MESSAGE_PREFIX}${authUser?.screenName || "User"} left the group`,
          threadIds: [messageThread.id],
        },
      }).catch((e) => console.warn("Failed to send system message:", e));
      // Hide the thread so it disappears from the list immediately
      try {
        await providers.MessageThreadsProvider.hide({ id: messageThread.id });
        removeCachedThread(String(messageThread.id)).catch(() => {});
      } catch (e) {
        console.warn("Failed to hide thread:", e);
      }
      emitter.emit(EVENTS.THREADS_REFRESH);
      if (onDeleted) onDeleted();
      history.push("/messages");
    },
    [messageThread.subject, messageThread.id, t, authUser, onDeleted, history]
  );

  const handleFolderToggle = useCallback(
    (e: React.MouseEvent, folderId: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (onToggleThreadInFolder) {
        onToggleThreadInFolder(folderId, String(messageThread.id));
      }
    },
    [onToggleThreadInFolder, messageThread.id]
  );

  const handleNewFolderSubmit = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (newFolderName.trim() && onCreateFolder) {
        const created = await onCreateFolder(newFolderName.trim());
        // Auto-add this chat to the newly created folder
        if (created && onToggleThreadInFolder) {
          onToggleThreadInFolder(created.id, String(messageThread.id));
        }
        setNewFolderName("");
        setShowNewFolderInput(false);
        setCtxMenu(null);
      }
    },
    [newFolderName, onCreateFolder, onToggleThreadInFolder, messageThread.id]
  );

  // Chat-list row reflects the group-wide rollup (General + topics) via the
  // aggregated* fields; falls back to the thread's own values for personal threads
  // / older cached data.
  const unreadCount = messageThread.aggregatedUnreadCount ?? messageThread.unreadMessageCount;
  const hasMention = (messageThread.aggregatedHasMention ?? messageThread.hasMention) === true;

  // Tip badge from Redis flag (persists even if tip is not the last message)
  const tipAmount = messageThread.tipAmount || null;

  const lastMessageText = useMemo(() => {
    const msg = messageThread.lastMessage;
    if (!msg) return "";
    if (msg.text) {
      if (msg.text.startsWith(SYSTEM_MESSAGE_PREFIX)) {
        // Full cleaner: strips [[insights:...]] markers / raw URLs and
        // localises known templates ("X added Y to the group", "Meeting
        // insights ready (…)"), which used to leak raw into the sidebar.
        return cleanMessagePreview(msg.text, t);
      }
      // Parse call JSON messages for sidebar preview
      const trimmedText = msg.text.trim();
      if (trimmedText.startsWith("{") && trimmedText.includes('"type":"call"')) {
        try {
          const callData = JSON.parse(trimmedText);
          if (callData && callData.type === "call" && callData.status) {
            if (callData.status === "ENDED") {
              if (callData.startTime && callData.endTime) {
                const ms = new Date(callData.endTime).getTime() - new Date(callData.startTime).getTime();
                const sec = Math.round(ms / 1000);
                const dur = formatCallDurationLocalized(sec);
                return `📞 ${t("Call ({{duration}})", { duration: dur })}`;
              }
              return `📞 ${t("Call ended")}`;
            }
            if (callData.status === "CANCELLED") return `📞 ${t("Missed call")}`;
            if (callData.status === "DECLINED") return `📞 ${t("Call declined")}`;
            return `📞 ${t("Call")}`;
          }
        } catch {
          /* not JSON */
        }
      }
      let text = msg.text;
      // Strip __META__ prefix for display
      let metaJson: any = null;
      if (text.startsWith("__META__:")) {
        const nlIdx = text.indexOf("\n");
        try {
          metaJson = JSON.parse(text.substring(9, nlIdx >= 0 ? nlIdx : undefined));
        } catch {
          /* ignore */
        }
        text = nlIdx >= 0 ? text.substring(nlIdx + 1) : "";
      }
      if (metaJson?.voiceMessage) return `🎤 ${t("Voice message")}`;
      if (!text) return t("Message");
      // Shared cleaner: strips markdown/mentions/[[...]] markers exactly like
      // every other preview surface, so they can't drift apart again.
      return cleanMessagePreview(text, t);
    }
    if (msg.attachment && msg.attachment.length > 0) return `📎 ${t("Attachment")}`;
    return "";
  }, [messageThread.lastMessage, t]);

  const timeStr = useMemo(() => {
    const msg = messageThread.lastMessage;
    if (!msg?.time) return "";
    return formatThreadTime(msg.time, t);
  }, [messageThread.lastMessage, t]);

  const linkTo = isGroup
    ? generatePath(Routes.groupMessages, {
        groupId: messageThread.groupSlug || messageThread.subject?.subjectId || "0",
      })
    : `/messages/${senderUser?.slug || senderUser?.id || authUser.id}`;

  const displayName = isGroup
    ? groupName || t("Group ({{count}})", { count: messageThread.userIds?.length || 0 })
    : senderUser?.screenName;

  const groupInitial = isGroup ? (groupName ? groupName.charAt(0).toUpperCase() : "G") : null;
  const groupAvatarSrc =
    isGroup && messageThread.groupAvatarUrl
      ? MediaFilesController.getImageThumbnail(messageThread.groupAvatarUrl, "240p")
      : null;

  const hasFolders = folders && folders.length > 0;

  return (
    <NavLink
      to={linkTo}
      className="message-thread-item"
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Portal the menu to document.body. .page-messages__sidebar has
          `will-change: transform` (PageMessages/style.scss) which per CSS
          spec makes it the containing block for ANY position:fixed
          descendant — so the menu's `top: clientY` would be interpreted
          relative to the sidebar's box, not the viewport, shifting it
          150–250 px below the click. Portaling to body bypasses every
          ancestor containing-block trap. Same pattern as the analogous
          MessageBubble ctx menu (MessageBubble/index.tsx:473). */}
      {ctxMenu &&
        createPortal(
          <div
            ref={menuRef}
            className="message-thread-item__ctx-menu"
            style={{
              top: menuPos?.top ?? ctxMenu.y,
              left: menuPos?.left ?? ctxMenu.x,
              // Hide the very first paint (raw click position) until the
              // useLayoutEffect has measured and possibly flipped the menu.
              // visibility:hidden still triggers layout so we get a real rect.
              visibility: menuPos ? "visible" : "hidden",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {(hasFolders || onCreateFolder) && (
              <div className="message-thread-item__ctx-folder-parent">
                <button
                  type="button"
                  className="message-thread-item__ctx-folder-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowFolderSub((v) => !v);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: 8, fill: "var(--msg-accent, #37a8ff)", flexShrink: 0 }}
                  >
                    <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                  </svg>
                  {t("Add to folder")}
                  <svg
                    viewBox="0 0 8 12"
                    width="6"
                    height="10"
                    style={{ marginLeft: "auto", fill: "var(--palette-neutral, #999)", flexShrink: 0 }}
                  >
                    <path d="M1.5 0L0 1.5 4.5 6 0 10.5 1.5 12 7.5 6z" />
                  </svg>
                </button>
                {showFolderSub && (
                  <div
                    className={`message-thread-item__ctx-folder-sub${
                      ctxMenu && ctxMenu.x > window.innerWidth / 2 ? " __left" : ""
                    }`}
                  >
                    {folders &&
                      folders.map((folder) => {
                        const isInFolder = threadFolderIds?.includes(folder.id);
                        return (
                          <button
                            key={folder.id}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={!!isInFolder}
                            onClick={(e) => handleFolderToggle(e, folder.id)}
                          >
                            <span
                              className={`message-thread-item__ctx-check${isInFolder ? " __checked" : ""}`}
                              aria-hidden="true"
                            >
                              {isInFolder ? "✓" : ""}
                            </span>
                            {folder.name}
                          </button>
                        );
                      })}
                    {onCreateFolder && !showNewFolderInput && (
                      <button
                        type="button"
                        className="message-thread-item__ctx-new-folder"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowNewFolderInput(true);
                        }}
                      >
                        + {t("New folder")}
                      </button>
                    )}
                    {showNewFolderInput && (
                      <div className="message-thread-item__ctx-new-folder-input" onClick={(e) => e.stopPropagation()}>
                        <input
                          autoFocus
                          placeholder={t("Folder name")}
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleNewFolderSubmit(e);
                            if (e.key === "Escape") setShowNewFolderInput(false);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {isGroup && (
              <button type="button" onClick={handleLeaveGroup}>
                {t("Leave Group")}
              </button>
            )}
            <button type="button" onClick={handleHideChat}>
              {t("Hide Chat")}
            </button>
            <button type="button" onClick={handleDeleteChat} style={{ color: "#e53935" }}>
              {t("Delete Chat")}
            </button>
          </div>,
          document.body
        )}
      <div className="message-thread-item__avatar-wrapper">
        {isGroup ? (
          <div className="message-thread-item__group-avatar">
            {groupAvatarSrc ? (
              <img src={groupAvatarSrc} alt={groupName || ""} className="message-thread-item__group-avatar-img" />
            ) : (
              groupInitial
            )}
          </div>
        ) : (
          <BaseAvatar user={senderUser} size={50} />
        )}
        {isOnline && <span className="message-thread-item__online-dot" />}
      </div>
      <div className="message-thread-item__content">
        <div className="message-thread-item__top-row">
          <span className="message-thread-item__name">{displayName}</span>
          {timeStr && <span className="message-thread-item__time">{timeStr}</span>}
        </div>
        <div className="message-thread-item__bottom-row">
          <span className="message-thread-item__preview">
            {draftText ? (
              <>
                <span className="message-thread-item__draft-label">{t("Draft")}:</span>{" "}
                <span className="message-thread-item__draft-text">{draftText}</span>
              </>
            ) : (
              lastMessageText
            )}
          </span>
          {tipAmount && <span className="message-thread-item__tip-badge">+{tipAmount} Cr</span>}
          {hasMention && <span className="message-thread-item__badge __mention">@</span>}
          {unreadCount > 0 && (
            <span className="message-thread-item__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
          )}
        </div>
      </div>
    </NavLink>
  );
}

export default React.memo(MessageThreadItem);
